import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Button,
  Box,
  Divider
} from '@mui/material';
import closeIcon from '../../../assets/icons/closeIcon.svg';
import { TypographyH4White } from '../../../features/malnutritionScreening/style';
import { handleImageDownload } from '../../utils/utilFunctions';

interface SimpleDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  content: string;
  srcImage?: string;
  imageHeight?: string;
  megaTitle?: string;
  downloadOptionRequired?: boolean;
  imageAlt?: string;
  imageOverflow?: string;
}

export const MixedModal = (props: SimpleDialogProps) => {
  const {
    onClose,
    open,
    content,
    title,
    srcImage,
    imageHeight,
    megaTitle,
    downloadOptionRequired,
    imageAlt
  } = props;

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="paper"
      maxWidth="xs"
      // Center the modal with padding around the viewport edges.
      sx={{
        '& .MuiDialog-container': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }
      }}
      PaperProps={{
        sx: {
          width: '400px',
          maxHeight: 'calc(100vh - 32px)',
          borderRadius: '0.9375rem',
          padding: '0.9375rem',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Close Button */}
      <Button
        sx={{ justifyContent: 'end' }}
        onClick={handleClose}
        data-testid="#mix-modal-close-btn"
      >
        <img src={closeIcon} alt="Close" />
      </Button>

      {/* Image Section */}
      {srcImage && (
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            padding: 0,
            overflow: 'auto',
            // Firefox scrollbar styling
            scrollbarWidth: 'thin',
            // Webkit scrollbar styling (Chrome, Safari, Edge)
            '&::-webkit-scrollbar': {
              width: '0.5rem'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '0.25rem'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '0.25rem'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555'
            }
          }}
        >
          <Box
            component="img"
            src={srcImage}
            alt={imageAlt}
            sx={{
              width: '100%',
              height: imageHeight ? imageHeight : 'auto',
              objectFit: 'contain',
              borderRadius: '0.25rem'
            }}
          />
        </DialogContent>
      )}

      {/* Mega Title Section (if provided) */}
      {megaTitle && (
        <>
          <DialogTitle sx={{ padding: '0 0 0 1.5rem' }}>
            <Typography
              sx={{
                fontFamily: 'Calibri',
                color: '#002A3A'
              }}
              dangerouslySetInnerHTML={{ __html: megaTitle }}
            />
          </DialogTitle>
          <Divider
            sx={{
              backgroundColor: 'rgba(255,255,255,0.4)',
              my: 2,
              width: '90%',
              mx: 'auto'
            }}
          />
        </>
      )}

      {/* Title Section */}
      <DialogTitle sx={{ padding: '0 0 0 1.5rem', marginTop: '0.625rem' }}>
        <Typography
          sx={{
            fontFamily: 'Calibri',
            fontSize: '1rem',
            fontWeight: 'bold',
            fontStyle: 'italic',
            color: '#004F71'
          }}
        >
          {title}
        </Typography>
      </DialogTitle>

      {/* Text Content Section */}
      <DialogContent sx={{ overflowY: 'visible' }}>
        <DialogContentText id="alert-dialog-description">
          <Typography
            sx={{
              fontFamily: 'Calibri',
              fontSize: '1rem',
              fontWeight: 450,
              color: '#004F71'
            }}
          >
            {content}
          </Typography>
        </DialogContentText>
      </DialogContent>

      {/* Download Button Section */}
      {downloadOptionRequired && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            marginTop: '0px'
          }}
        >
          <Button
            variant="outlined"
            sx={{
              marginBottom: 2,
              height: '2.5rem',
              padding: '0 0.9375rem',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              textAlign: 'center',
              lineHeight: 'normal',
              borderRadius: '100px',
              backgroundColor: '#007ACC',
              color: '#FFF'
            }}
            onClick={() => handleImageDownload(srcImage)}
            data-testid="#mix-modal-download-btn"
          >
            <TypographyH4White sx={{ margin: '0.625rem' }}>
              Download Image
            </TypographyH4White>
          </Button>
        </Box>
      )}
    </Dialog>
  );
};
